import { FocusEventHandler, Fragment, ReactElement, useState } from 'react';

import { Switch as HeadlessSwitch } from '@headlessui/react';
import styled from 'styled-components';

import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useTranslate } from '@locale/translate';
import { Spacing, theme } from '@styles/theme';

import { AnimatedSwitch } from './animated-switch';

import { VisuallyHidden } from '../visually-hidden';

export type SwitchProps = {
  defaultValue?: boolean;
  onChange?(value: boolean): void;
  onBlur?: FocusEventHandler;
  label?: string;
  labelSide?: 'left' | 'right';
  labelVisible?: boolean;
  isDisabled?: boolean;
  error?: string;
  value?: boolean;
  dataCy?: string;
  iconLeft?: ReactElement<IconProps>;
  size?: 'sm' | 'md';
  gap?: Spacing;
};
export function Switch({
  defaultValue = false,
  label,
  labelSide = 'right',
  labelVisible = true,
  isDisabled = false,
  onChange,
  error,
  value,
  dataCy,
  iconLeft,
  size = 'md',
  gap = 2,
  ...rest
}: SwitchProps) {
  const isControlled = value !== undefined;

  const [localChecked, setLocalChecked] = useState(isControlled ? value : defaultValue);
  const checked = isControlled ? value : localChecked;
  const LabelContainer = labelVisible ? Fragment : VisuallyHidden;

  return (
    <Stack space={1} style={{ minWidth: 0 }}>
      <HeadlessSwitch.Group>
        <HeadlessSwitch
          data-cy={dataCy}
          as={IndicatorContainer}
          $isDisabled={isDisabled}
          checked={checked}
          onChange={() => {
            if (!isDisabled) {
              setLocalChecked(!checked);
              onChange?.(!checked);
            }
          }}
          {...rest}
        >
          <Stack
            orientation="horizontal"
            justify="flex-start"
            align="center"
            space={gap}
            isReverse={labelSide === 'left'}
            style={{ overflow: 'hidden' }}
          >
            <AnimatedSwitch size={size} checked={checked} isDisabled={isDisabled} />
            {iconLeft}
            <LabelContainer>
              <HeadlessSwitch.Label as={Label} $isDisabled={isDisabled}>
                <Text text={label} font={size === 'sm' ? 'regular12' : 'regular14'} />
              </HeadlessSwitch.Label>
            </LabelContainer>
          </Stack>
        </HeadlessSwitch>
      </HeadlessSwitch.Group>
      <FormError text={error} />
    </Stack>
  );
}

export type LabeledSwitchProps = {
  defaultValue?: boolean;
  onChange?(value: boolean): void;
  onBlur?: FocusEventHandler;
  onText?: string;
  offText?: string;
  label: string;
  isDisabled?: boolean;
  value?: boolean;
  dataCy?: string;
  helpText?: string;
  helpTextPosition?: 'right' | 'bottom';
};

export const LabeledSwitch = ({
  value,
  onChange,
  label,
  onText: userOnText,
  offText: userOffText,
  defaultValue,
  dataCy,
  isDisabled,
  onBlur,
  helpText = '',
  helpTextPosition = 'right',
}: LabeledSwitchProps) => {
  const { t } = useTranslate();
  const [localChecked, setLocalChecked] = useState(value ?? defaultValue ?? false);
  const checked = value ?? localChecked;

  const onText = userOnText ?? t('core.yes');
  const offText = userOffText ?? t('core.no');

  return (
    <HeadlessSwitch.Group as="div">
      <Stack orientation="horizontal" justify="space-between" align="center">
        <div>
          <HeadlessSwitch.Label
            as={FormLabel}
            text={label}
            helpText={helpTextPosition === 'right' ? helpText : undefined}
          />
          {helpTextPosition === 'bottom' && helpText && (
            <Text font="regular14" color="textHelper" text={helpText} />
          )}
          {helpTextPosition !== 'bottom' && (
            <HeadlessSwitch.Description as={Fragment}>
              <Text
                font="regular14"
                color={checked ? 'textPrimary' : 'textHelper'}
                text={checked ? onText : offText}
              />
            </HeadlessSwitch.Description>
          )}
        </div>
        <HeadlessSwitch
          disabled={isDisabled}
          as={IndicatorContainer}
          $isDisabled={isDisabled}
          data-cy={dataCy}
          checked={checked}
          onChange={() => {
            if (!isDisabled) {
              setLocalChecked(!checked);
              onChange?.(!checked);
            }
          }}
          onBlur={onBlur}
        >
          <AnimatedSwitch checked={checked} isDisabled={isDisabled} />
        </HeadlessSwitch>
      </Stack>
    </HeadlessSwitch.Group>
  );
};

const Label = styled.label<{ $isDisabled: boolean }>((p) => ({
  cursor: p.$isDisabled ? 'not-allowed' : 'pointer',
  font: theme.font.regular14,
  color: theme.form.label.color,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const IndicatorContainer = styled.button<{ $isDisabled: boolean }>((p) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: p.$isDisabled ? 'not-allowed' : 'pointer',
  height: '1.5rem',
  opacity: p.$isDisabled ? 0.5 : 1,
  outline: 'none',
}));
